/**
 * Slot derivation from a match's video layout.
 *
 * A match's `format` field is the VIDEO layout — `"full"` (one
 * full-match video) or `"separate"` (two halves plus optional extra-time
 * and shootout recordings; legacy `"two_halves"` has the same slot set). It is set at match-creation time and editable via the
 * match-edit modal. Despite the field name it is NOT the game
 * format (7v7 / 9v9 / 11v11 — that lives on the team as
 * `game_format`).
 *
 * Historically the slot pickers (Coach Review's `MatchSlotPicker`,
 * the upload modal) hardcoded all three slots regardless of the
 * match. A coach could pick `full` for a `two_halves` match whose
 * `full` video was never transcoded — the HLS / MP4 request 404s
 * and the canvas shows nothing. These helpers gate the slot set on
 * the match's actual layout so an absent slot is never offered.
 */

import type { Match } from "@/lib/types";

/** A selectable video slot — mirrors `_VALID_SLOTS` in models.py. */
export type MatchSlot =
  "full" | "first_half" | "second_half" | "extra_time" | "penalty_shootout";

/** Display labels for each slot. */
export const SLOT_LABELS: Record<MatchSlot, string> = {
  full: "Full match",
  first_half: "First half",
  second_half: "Second half",
  extra_time: "Extra time",
  penalty_shootout: "Penalty shootout",
};

const FULL_SLOTS: readonly MatchSlot[] = ["full"];
const HALVES_SLOTS: readonly MatchSlot[] = [
  "first_half",
  "second_half",
  "extra_time",
  "penalty_shootout",
];

/**
 * The slots a match actually has, given its `format`.
 *
 * `"separate"` / legacy `"two_halves"` → `["first_half", "second_half", "extra_time", "penalty_shootout"]`.
 * Anything else (`"full"`, legacy/missing) → `["full"]` — `"full"`
 * is the schema default, so an unknown value is treated as a
 * single full-match video.
 */
export function slotsForMatch(
  match: Pick<Match, "format"> | undefined | null,
): readonly MatchSlot[] {
  return match?.format === "two_halves" || match?.format === "separate"
    ? HALVES_SLOTS
    : FULL_SLOTS;
}

/**
 * The slot a freshly-selected match should default to — its first
 * available slot (`"full"` for a full match, `"first_half"` for a
 * two-halves match).
 */
export function defaultSlotForMatch(
  match: Pick<Match, "format"> | undefined | null,
): MatchSlot {
  return slotsForMatch(match)[0];
}

/** Whether `slot` is valid for the match's layout. */
export function isSlotValidForMatch(
  match: Pick<Match, "format"> | undefined | null,
  slot: string,
): boolean {
  return slotsForMatch(match).includes(slot as MatchSlot);
}

export function urlSlotToKey(urlSlot: string | undefined): string | undefined {
  return urlSlot ? urlSlot.replace(/-/g, "_") : undefined;
}
export function slotKeyToUrl(slotKey: string): string { return slotKey.replace(/_/g, "-"); }
